<script setup lang="ts">
import { computed, ref } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type {
  ManagementDuplicateChoice,
  ManagementSuggestion,
  ManagementSuggestionStatus,
  ManagementSuggestionValues,
} from '@/types/managementSuggestionTypes'

import AvatarInitials from '@/components/common/ui/avatar/AvatarInitials.vue'
import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import ManagementSuggestionDuplicateChoice from '@/components/editRelationGroup/managementSuggestions/ManagementSuggestionDuplicateChoice.vue'
import ManagementSuggestionEditForm from '@/components/editRelationGroup/managementSuggestions/ManagementSuggestionEditForm.vue'
import {
  canAcceptSuggestion,
  getSuggestionName,
  getUrlHost,
} from '@/utilities/managementSuggestions'
import { translationFunctionCommon, translationFunctionDefault } from '@/utilities/translations'

interface ManagementSuggestionRowProps {
  // create: accepted persons are created together with the organization; edit: accepting saves at once
  mode: 'create' | 'edit'
  roleOptions: DropdownOption<null | string>[]
  salutationOptions: DropdownOption<null | string>[]
  suggestion: ManagementSuggestion
  saving?: boolean | undefined
}

const props = defineProps<ManagementSuggestionRowProps>()
const emit = defineEmits<{
  accept: []
  discard: []
  reopen: []
  selectDuplicate: [choice: ManagementDuplicateChoice]
  update: [values: ManagementSuggestionValues]
}>()

const tfCommon = translationFunctionCommon()
const tfDefault = translationFunctionDefault()

const editing = ref(false)

const findLabel = (options: DropdownOption<null | string>[], value: null | string): string => {
  return value ? (options.find((option) => option.optionValue === value)?.optionLabel ?? value) : ''
}

const displayName = computed(() => {
  const salutation = findLabel(props.salutationOptions, props.suggestion.values.salutation)
  return [salutation, getSuggestionName(props.suggestion)].filter(Boolean).join(' ')
})

// The position names the concrete function from the source; the generic role label is only the fallback
const functionLabel = computed(
  () =>
    props.suggestion.values.position.trim() ||
    findLabel(props.roleOptions, props.suggestion.values.role) ||
    '-',
)

const sourceLabel = computed(() =>
  tfDefault(`managementSuggestions.source.${props.suggestion.sourceType}`),
)

// The link keeps the full URL; the row only names the host to stay readable in the narrow slide-in
const sourceHost = computed(() => getUrlHost(props.suggestion.sourceUrl))

const STATUS_TEXT_KEYS: Partial<Record<ManagementSuggestionStatus, string>> = {
  accepted: 'managementSuggestions.statusAccepted',
  discarded: 'managementSuggestions.statusDiscarded',
  existing: 'managementSuggestions.statusExisting',
  saved: 'managementSuggestions.statusSaved',
}

const statusText = computed(() => {
  if (
    props.suggestion.status === 'accepted' &&
    props.suggestion.duplicateChoice?.action === 'link'
  ) {
    return tfDefault('managementSuggestions.statusAcceptedLink')
  }
  const statusTextKey = STATUS_TEXT_KEYS[props.suggestion.status]
  return statusTextKey ? tfDefault(statusTextKey) : ''
})

const isMuted = computed(() => ['discarded', 'existing'].includes(props.suggestion.status))

const canUndo = computed(() => {
  const { status } = props.suggestion
  return status === 'discarded' || (props.mode === 'create' && status === 'accepted')
})

// The URL comes from web content; only real web links are rendered as a link
const sourceLink = computed(() => {
  const { sourceUrl } = props.suggestion
  return sourceUrl && /^https?:\/\//iu.test(sourceUrl) ? sourceUrl : null
})

const startEditing = (): void => {
  editing.value = true
}

const stopEditing = (): void => {
  editing.value = false
}

const onSave = (values: ManagementSuggestionValues): void => {
  emit('update', values)
  stopEditing()
}

const onAccept = (): void => {
  emit('accept')
}

const onDiscard = (): void => {
  emit('discard')
}

const onReopen = (): void => {
  emit('reopen')
}

const onSelectDuplicate = (choice: ManagementDuplicateChoice): void => {
  emit('selectDuplicate', choice)
}
</script>

<template>
  <li
    class="flex flex-col gap-3 border-b border-dotted border-black-400 py-4 last:border-none"
    :class="{ 'opacity-60': isMuted }"
  >
    <div class="flex items-start gap-4 text-sm">
      <AvatarInitials :name="getSuggestionName(suggestion)" size="big" />
      <div class="flex min-w-0 flex-1 flex-col">
        <span class="pb-1 font-medium text-black-900">{{ displayName }}</span>
        <span class="min-h-5">{{ functionLabel }}</span>
        <span class="text-black-500">
          {{ tfDefault('managementSuggestions.sourcePrefix') }}
          <a
            v-if="sourceLink"
            class="break-words text-lochmara-600 hover:underline"
            :href="sourceLink"
            rel="noopener noreferrer"
            target="_blank"
            :title="sourceLink"
          >
            {{ sourceLabel }} · {{ sourceHost }}
          </a>
          <template v-else>{{ sourceLabel }}</template>
        </span>
      </div>
    </div>
    <div class="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
      <span v-if="suggestion.matchesPending" class="text-sm text-black-500">
        {{ tfDefault('managementSuggestions.checkingMatches') }}
      </span>
      <template v-if="suggestion.status === 'open' && !editing">
        <ButtonTertiary :label="tfCommon('button.edit')" @click="startEditing" />
        <ButtonTertiary :label="tfDefault('managementSuggestions.discard')" @click="onDiscard" />
        <ButtonPrimary
          :disabled="!canAcceptSuggestion(suggestion)"
          :label="tfCommon('button.accept')"
          :loading="saving"
          small
          @click="onAccept"
        />
      </template>
      <template v-else-if="statusText">
        <span
          class="rounded-full px-2 py-0.5 text-xs"
          :class="isMuted ? 'bg-lynch-100 text-lynch-700' : 'bg-keppel-100 text-keppel-700'"
        >
          {{ statusText }}
        </span>
        <ButtonTertiary
          v-if="canUndo"
          :label="tfDefault('managementSuggestions.undo')"
          @click="onReopen"
        />
      </template>
    </div>
    <ManagementSuggestionDuplicateChoice
      v-if="suggestion.status === 'open' && suggestion.duplicates.length > 0"
      :choice="suggestion.duplicateChoice"
      :duplicates="suggestion.duplicates"
      :group-name="`management-duplicate-${suggestion.id}`"
      @select="onSelectDuplicate"
    />
    <ManagementSuggestionEditForm
      v-if="editing"
      :role-options="roleOptions"
      :salutation-options="salutationOptions"
      :values="suggestion.values"
      @cancel="stopEditing"
      @save="onSave"
    />
  </li>
</template>
